import { classifyFailure } from "@lafayette/api-client";

import { type CatalogFailure, describeCatalogError } from "../errors";

// How a failed place call is told to a staff member. The text comes from the HTTP status and the action
// only. An error body is never shown: it could carry provider text, and the contract says provider text
// must not reach a message, a log or storage.

export type PlaceAction = "read" | "search" | "select" | "clear" | "verify" | "location";

export type PlaceErrorContext = Readonly<{
  action: PlaceAction;
  /** From the place view: false when the server runs with `LAFAYETTE_PLACES=off`. */
  lookupAvailable?: boolean;
}>;

const NOT_CHANGED = "The item was not changed.";

export function describePlaceError(error: unknown, context: PlaceErrorContext): CatalogFailure {
  const api = classifyFailure(error);
  if (api.status !== null) {
    const base = { requestId: api.requestId };
    if (api.kind === "rate_limited") {
      return {
        ...base,
        kind: "unavailable",
        retryable: false,
        message: `The limit of place lookups is reached for now. Wait a few minutes and try again. ${NOT_CHANGED}`,
      };
    }
    if (api.kind === "unavailable" && context.lookupAvailable === false) {
      return {
        ...base,
        kind: "unavailable",
        retryable: false,
        message: "Place lookup is not configured.",
      };
    }
    if (
      api.kind === "unavailable" &&
      (context.action === "search" || context.action === "verify")
    ) {
      return {
        ...base,
        kind: "unavailable",
        retryable: false,
        message: `The place service did not answer. Try again in a moment. ${NOT_CHANGED}`,
      };
    }
    if (api.kind === "invalid" && context.action === "select") {
      return {
        ...base,
        kind: "invalid",
        retryable: false,
        message:
          "This result cannot be used any more: it expired, or it does not belong to the last search. Search again.",
      };
    }
    if (api.kind === "conflict" && context.action === "select") {
      return {
        ...base,
        kind: "conflict",
        retryable: false,
        message:
          "The server refused this match. The place may belong to another item, this item may already have a match, or the item changed.",
      };
    }
    if (api.kind === "conflict" && context.action === "location") {
      return {
        ...base,
        kind: "conflict",
        retryable: false,
        message:
          "The server refused this location. If the item is published, the change may leave it unable to be published (a published item needs a locality and a country).",
      };
    }
    if (api.kind === "conflict" && context.action === "verify") {
      return {
        ...base,
        kind: "conflict",
        retryable: false,
        message: "Only an item with a matched place can be verified.",
      };
    }
  }
  return describeCatalogError(error);
}
